import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Badge,
  ColorSwatch,
  Button,
  Card,
  ColorInput,
  Divider,
  Fieldset,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useProjectId } from "../hooks/useProjectId";
import {
  articleColoringApi,
  ZArticleColoringSettings,
  type ArticleColoringSettings,
} from "./articleColoringApi";

function SettingsEditor({
  projectNo,
  initial,
}: {
  projectNo: number;
  initial: ArticleColoringSettings;
}) {
  const [save, { isLoading }] = articleColoringApi.usePutMultiplacerProjectArticleColoringMutation();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const form = useForm<ArticleColoringSettings>({ initialValues: structuredClone(initial) });
  const dirty = form.isDirty();

  async function submit(values: ArticleColoringSettings) {
    setError(null);
    setSaved(false);
    const parsed = ZArticleColoringSettings.safeParse({
      ...values,
      articleColorings: values.articleColorings.map((item) => ({
        ...item,
        fallbackColor: item.fallbackColor?.trim() || undefined,
        values: item.values.map((value) => ({ ...value, label: value.label?.trim() || undefined })),
      })),
    });
    if (!parsed.success) {
      form.setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [
            issue.path.join("."),
            "Kontroller verdien i dette feltet",
          ])
        )
      );
      return;
    }
    const duplicate = (items: string[]) => new Set(items).size !== items.length;
    if (
      duplicate(values.articleColorings.map((item) => item.id)) ||
      duplicate(values.drofusArticleProperties.map((item) => item.key)) ||
      values.articleColorings.some((item) => duplicate(item.values.map((value) => value.value)))
    ) {
      setError("Filter-ID, egenskapsnøkler og verdier i hvert filter må være unike.");
      return;
    }
    try {
      const result = structuredClone(await save({ body: { projectNo, ...parsed.data } }).unwrap());
      form.setInitialValues(result);
      form.setValues(result);
      form.resetDirty(result);
      setSaved(true);
      setEditing(false);
    } catch {
      setError("Kunne ikke lagre innstillingene. Endringene er beholdt. Prøv igjen.");
    }
  }

  if (!editing)
    return (
      <Stack>
        <Group justify="space-between">
          <Text fw={600}>Fargefiltre</Text>
          <Button
            variant="neutral"
            onClick={() => {
              const latest = structuredClone(initial);
              form.setInitialValues(latest);
              form.setValues(latest);
              form.resetDirty(latest);
              form.clearErrors();
              setSaved(false);
              setEditing(true);
            }}
          >
            Rediger innstillinger
          </Button>
        </Group>
        {saved && <Alert color="green">Innstillingene er lagret.</Alert>}
        {form.values.articleColorings.length === 0 && (
          <Text c="dimmed" size="sm">
            Ingen fargefiltre er konfigurert.
          </Text>
        )}
        {form.values.articleColorings.map((coloring) => {
          const source = form.values.drofusArticleProperties.find(
            (property) => property.key === coloring.propertyKey
          );
          return (
            <Card withBorder radius="md" key={coloring.id}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>{coloring.label}</Text>
                  <Badge variant="light" color={source ? "blue" : "gray"}>
                    {source ? "dRofus" : "Ingen dRofus-kobling"}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {source ? "dRofus → " : "Egenskap: "}
                  {coloring.propertyKey} → {coloring.label}
                </Text>
                {source && (
                  <details>
                    <summary style={{ cursor: "pointer" }}>Vis kildesfelt</summary>
                    <Text size="xs" mt="xs" style={{ overflowWrap: "anywhere" }}>
                      {source.occurrenceField}
                    </Text>
                  </details>
                )}
                {coloring.values.map((value) => (
                  <Group key={value.value} gap="sm">
                    <ColorSwatch
                      color={value.color}
                      size={20}
                      aria-label={`Farge ${value.color}`}
                    />
                    <Text size="sm" fw={500}>
                      {value.value}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {value.label}
                    </Text>
                  </Group>
                ))}
                <Group gap="sm">
                  {coloring.fallbackColor && <ColorSwatch color={coloring.fallbackColor} size={20} />}
                  <Text size="sm">
                    Ingen treff: {coloring.fallbackColor ?? "Behold objektets farge"}
                  </Text>
                </Group>
                {coloring.values.length === 0 && (
                  <Text size="sm" c="dimmed">
                    Ingen fargeverdier.
                  </Text>
                )}
              </Stack>
            </Card>
          );
        })}
        <Text size="xs" c="dimmed">
          Reservefargen brukes når objektet mangler verdi eller verdien ikke har en konfigurert farge.
        </Text>
      </Stack>
    );

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack>
        <Fieldset legend="Fargefiltre" disabled={isLoading}>
          <Stack>
            <Text size="sm" c="dimmed">
              Objekter kobles til artikler via artikkelnummer. Velg eventuelt en reservefarge for objekter uten treff.
            </Text>
            {form.values.articleColorings.length === 0 && (
              <Text size="sm">Ingen fargefiltre er konfigurert.</Text>
            )}
            {form.values.articleColorings.map((coloring, index) => {
              const path = `articleColorings.${index}`;
              const sourceIndex = form.values.drofusArticleProperties.findIndex(
                (property) => property.key === coloring.propertyKey
              );
              const source = form.values.drofusArticleProperties[sourceIndex];
              const propertyOptions = Array.from(
                new Set([
                  ...form.values.drofusArticleProperties.map((property) => property.key),
                  ...form.values.articleColorings.map((item) => item.propertyKey),
                ])
              ).filter(Boolean);
              return (
                <Card withBorder key={index} p="sm">
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text fw={500}>{coloring.label || "Nytt fargefilter"}</Text>
                      <Group gap="xs">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          disabled={index === 0}
                          onClick={() =>
                            form.reorderListItem("articleColorings", { from: index, to: index - 1 })
                          }
                        >
                          Flytt opp
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          disabled={index === form.values.articleColorings.length - 1}
                          onClick={() =>
                            form.reorderListItem("articleColorings", { from: index, to: index + 1 })
                          }
                        >
                          Flytt ned
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          onClick={() => form.removeListItem("articleColorings", index)}
                        >
                          Fjern filter
                        </Button>
                      </Group>
                    </Group>
                    <TextInput
                      label="Navn"
                      placeholder="AR-status"
                      {...form.getInputProps(`${path}.label`)}
                    />
                    <Autocomplete
                      label="Egenskap"
                      description="Velg en konfigurert egenskap, eller skriv en nøkkel for en annen datakilde."
                      data={propertyOptions}
                      placeholder="Velg egenskap"
                      {...form.getInputProps(`${path}.propertyKey`)}
                    />
                    <Card withBorder radius="md" p="sm" bg="var(--mantine-color-body)">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>
                          Datakilde: {source ? "dRofus" : "Ingen dRofus-kobling"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {coloring.propertyKey || "Velg egenskap"} →{" "}
                          {coloring.label || "Fargefilter"}
                        </Text>
                        {source ? (
                          <details>
                            <summary style={{ cursor: "pointer" }}>Rediger kildekobling</summary>
                            <Text size="xs" c="dimmed" mt="sm">
                              Koblingen deles av alle filtre som bruker {source.key}.
                            </Text>
                            <TextInput
                              mt="xs"
                              label="dRofus-felt"
                              {...form.getInputProps(
                                `drofusArticleProperties.${sourceIndex}.occurrenceField`
                              )}
                            />
                            <Button
                              variant="subtle"
                              color="red"
                              size="xs"
                              mt="xs"
                              onClick={() =>
                                form.removeListItem("drofusArticleProperties", sourceIndex)
                              }
                            >
                              Fjern kildekobling
                            </Button>
                          </details>
                        ) : (
                          <Button
                            variant="light"
                            size="xs"
                            disabled={!/^[a-z][a-zA-Z0-9_]*$/.test(coloring.propertyKey)}
                            onClick={() =>
                              form.insertListItem("drofusArticleProperties", {
                                key: coloring.propertyKey,
                                occurrenceField: "",
                              })
                            }
                          >
                            Koble til dRofus
                          </Button>
                        )}
                        {source &&
                          form.errors[`drofusArticleProperties.${sourceIndex}.occurrenceField`] && (
                            <Text c="red" size="xs">
                              Åpne kildekoblingen og fyll inn et gyldig dRofus-felt.
                            </Text>
                          )}
                      </Stack>
                    </Card>
                    <details>
                      <summary style={{ cursor: "pointer" }}>Avansert</summary>
                      <TextInput
                        mt="xs"
                        label="Filter-ID"
                        description="Stabil ID uten mellomrom"
                        {...form.getInputProps(`${path}.id`)}
                      />
                    </details>
                    {form.errors[`${path}.id`] && (
                      <Text c="red" size="xs">
                        Kontroller filter-ID under Avansert.
                      </Text>
                    )}
                    <ColorInput
                      label="Farge ved ingen treff (valgfritt)"
                      description="Brukes når artikkel eller verdi mangler, er null, eller ikke matcher noen av verdiene nedenfor. La feltet være tomt for å beholde objektets farge."
                      placeholder="Behold objektets farge"
                      format="hex"
                      {...form.getInputProps(`${path}.fallbackColor`)}
                      value={coloring.fallbackColor ?? ""}
                    />
                    {coloring.values.map((_value, valueIndex) => (
                      <Group key={valueIndex} align="start" wrap="wrap">
                        <TextInput
                          style={{ flex: 1, minWidth: 100 }}
                          label="Verdi"
                          placeholder="Ar0"
                          {...form.getInputProps(`${path}.values.${valueIndex}.value`)}
                        />
                        <TextInput
                          style={{ flex: 1, minWidth: 100 }}
                          label="Visningsnavn (valgfritt)"
                          placeholder="Planartikkel"
                          {...form.getInputProps(`${path}.values.${valueIndex}.label`)}
                        />
                        <ColorInput
                          style={{ flex: 1, minWidth: 130 }}
                          label="Farge"
                          format="hex"
                          {...form.getInputProps(`${path}.values.${valueIndex}.color`)}
                        />
                        <Button
                          mt={25}
                          variant="subtle"
                          color="red"
                          aria-label={`Fjern verdi ${valueIndex + 1} fra ${coloring.label || "filter"}`}
                          onClick={() => form.removeListItem(`${path}.values`, valueIndex)}
                        >
                          Fjern
                        </Button>
                      </Group>
                    ))}
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() =>
                        form.insertListItem(`${path}.values`, {
                          value: "",
                          label: "",
                          color: "#536BC7",
                        })
                      }
                    >
                      Legg til verdi
                    </Button>
                  </Stack>
                </Card>
              );
            })}
            <Button
              variant="light"
              onClick={() =>
                form.insertListItem("articleColorings", {
                  id: `filter_${crypto.randomUUID().replaceAll("-", "")}`,
                  label: "",
                  propertyKey: "",
                  values: [],
                })
              }
            >
              Legg til fargefilter
            </Button>
          </Stack>
        </Fieldset>
        <details>
          <summary style={{ cursor: "pointer" }}>
            Administrer alle dRofus-egenskaper ({form.values.drofusArticleProperties.length})
          </summary>
          <Fieldset legend="Artikkelegenskaper fra dRofus" disabled={isLoading}>
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Koble en egenskapsnøkkel til et artikkelfelt i dRofus. Fargefiltre kan også bruke
                egenskaper fra andre datakilder.
              </Text>
              {form.values.drofusArticleProperties.length === 0 && (
                <Text size="sm">Ingen dRofus-felt er konfigurert.</Text>
              )}
              {form.values.drofusArticleProperties.map((_property, index) => (
                <Card withBorder key={index} p="sm">
                  <Stack gap="xs">
                    <TextInput
                      label="Egenskapsnøkkel"
                      placeholder="arStatus"
                      {...form.getInputProps(`drofusArticleProperties.${index}.key`)}
                    />
                    <TextInput
                      label="dRofus-felt"
                      description="Artikkelfelt via forekomster. Feltet må starte med article_id_."
                      placeholder="article_id_article_classification_146_classification_entry_id_code"
                      {...form.getInputProps(`drofusArticleProperties.${index}.occurrenceField`)}
                    />
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => form.removeListItem("drofusArticleProperties", index)}
                    >
                      Fjern kobling
                    </Button>
                  </Stack>
                </Card>
              ))}
              <Button
                variant="light"
                onClick={() =>
                  form.insertListItem("drofusArticleProperties", { key: "", occurrenceField: "" })
                }
              >
                Legg til dRofus-felt
              </Button>
            </Stack>
          </Fieldset>
        </details>
        <Divider />
        {error && <Alert color="red">{error}</Alert>}
        {saved && !dirty && <Alert color="green">Innstillingene er lagret.</Alert>}
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {dirty ? "Du har ulagrede endringer." : ""}
          </Text>
          <Group>
            <Button
              variant="neutral"
              disabled={isLoading}
              onClick={() => {
                form.reset();
                setError(null);
                setSaved(false);
                setEditing(false);
              }}
            >
              Avbryt
            </Button>
            <Button type="submit" loading={isLoading} disabled={!dirty}>
              Lagre innstillinger
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
}

export default function ArticleColoringSettingsCard() {
  const projectNo = useProjectId();
  const { currentData, isFetching, isError, refetch } =
    articleColoringApi.useGetMultiplacerProjectArticleColoringQuery({ projectNo });
  return (
    <Card withBorder radius="lg" p="md">
      <Stack>
        <Text fw={600}>Artikkelfarger</Text>
        <Text size="sm" c="dimmed">
          Definer fargefiltre for prosjektets modellobjekter.
        </Text>
        <Alert color="blue">
          Fargefiltrene er tilgjengelige under Komponenter i visningen. Egenskapsverdier hentes
          fra siste komplette artikkelimport.
        </Alert>
        {isError ? (
          <Alert color="red">
            Kunne ikke hente innstillingene.{" "}
            <Button variant="subtle" onClick={() => void refetch()}>
              Prøv igjen
            </Button>
          </Alert>
        ) : currentData ? (
          <SettingsEditor key={projectNo} projectNo={projectNo} initial={currentData} />
        ) : isFetching ? (
          <Loader size="sm" />
        ) : null}
      </Stack>
    </Card>
  );
}
